import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StockTableLayout } from '../../components/StockTable';
import { TickerDisplayData } from '../../types/ticker';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { userTickerListApi } from '../../services/userTickerListApi';
import TabBar from '../../components/Common/TabBar';
import DeleteListConfirmationModal from '../../components/Modals/DeleteListConfirmationModal';
import ErrorDialog from '../../components/Modals/ErrorDialog';
import ShareListModal from '../../components/ShareListModal';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import {
  PERFORMANCE_STATISTICS,
  CATEGORY_HEADERS,
  TickerPerformanceData,
  PerformanceRow,
} from '../../utils/performanceTabUtils';
import { formatPerformanceStatisticValue, getPerformanceStatisticColor } from '../../hooks/usePerformanceTabStatistics';
import { publicListApi, PublicListTickerMapping, PublicListPreferences } from '../../services/publicListApi';
import { useListPreferences } from '../../hooks/useListPreferences';
import { useUserListManagement } from '../../hooks/useUserListManagement';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useEditMode } from '../../hooks/useEditMode';
import { useAddTicker } from '../../hooks/useAddTicker';
import { useModals } from '../../hooks/useModals';
import { userApi } from '../../services/userApi';
import { sessionSaveService } from '../../services/sessionSaveService';
import {
  clearListCache,
  clearCurrentListIdCache,
  clearCurrentListNameCache,
  cacheCurrentListId,
  cacheCurrentListName,
} from '../../utils/listCache';

// Default tickers to display
const DEFAULT_TICKERS = ['NVDA', 'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'META', 'TSLA'];

const ListPerformance: React.FC = () => {
  useEffect(() => {
    document.title = 'Stock Table - Performance';
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;

  // Public list state
  const [publicListData, setPublicListData] = useState<{
    publicListName: string | null;
    tickerMappings: PublicListTickerMapping[];
    listPreferences: PublicListPreferences | null;
    ownerHasProSubscription: boolean;
  } | null>(null);

  const [isLoadingPublicList, setIsLoadingPublicList] = useState(false);
  const [publicListError, setPublicListError] = useState<string | null>(null);
  const [isPublicListNotFound, setIsPublicListNotFound] = useState(false);

  // UI state
  const [tickerList, setTickerList] = useState<string[]>(() => {
    if (isPublicList) return [];
    if (isAuthenticated) return [];
    const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
    return savedTickers ? JSON.parse(savedTickers) : [];
  });

  const [listName, setListName] = useState<string>(() => {
    if (isPublicList && publicUuid) {
      const cachedPublicListName = sessionStorage.getItem(`public-list-name-${publicUuid}`);
      return cachedPublicListName || '';
    }
    const savedListName = sessionStorage.getItem('stocktable-list-name');
    return savedListName || 'Main List';
  });

  const refreshUsageLimitsRef = useRef<(() => void) | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const originalSessionStorageRef = useRef<{
    tickerList: string | null;
    listName: string | null;
    hiddenStatistics: string | null;
  } | null>(null);

  const publicListFetchAttemptedRef = useRef<string | null>(null);

  // Fetch public list data (same as ListTechnicals)
  useEffect(() => {
    const shouldFetch =
      publicUuid &&
      !publicListData &&
      !isLoadingPublicList &&
      publicListFetchAttemptedRef.current !== publicUuid;

    if (shouldFetch) {
      if (!isAuthenticated && !originalSessionStorageRef.current) {
        originalSessionStorageRef.current = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list'),
          listName: sessionStorage.getItem('stocktable-list-name'),
          hiddenStatistics: sessionStorage.getItem('stocktable-hidden-statistics'),
        };
      }

      publicListFetchAttemptedRef.current = publicUuid;
      setIsLoadingPublicList(true);
      setPublicListError(null);

      publicListApi
        .getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            publicListName: response.data.public_list_name,
            tickerMappings: response.data.ticker_mappings,
            listPreferences: response.data.list_preferences,
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });

          const tickers = response.data.ticker_mappings
            .sort((a, b) => a.order_num - b.order_num)
            .map((mapping: any) => mapping.ticker_symbol);
          setTickerList(tickers);

          const publicListName = response.data.public_list_name || 'Public List';
          setListName(publicListName);
          if (publicUuid) {
            sessionStorage.setItem(`public-list-name-${publicUuid}`, publicListName);
          }
        })
        .catch((error) => {
          const errorMessage = (error as any)?.message || 'Failed to load public list';
          setPublicListError(errorMessage);
          if ((error as any).status === 404 || String(errorMessage).includes('not found')) {
            setIsPublicListNotFound(true);
          } else if ((error as any).status === 403 || String(errorMessage).includes('not publicly shared')) {
            setIsPublicListNotFound(true);
          }
        })
        .finally(() => {
          setIsLoadingPublicList(false);
        });
    }
  }, [publicUuid, publicListData, isLoadingPublicList, isAuthenticated]);

  // Data state
  const [tickerPerformanceData, setTickerPerformanceData] = useState<TickerPerformanceData>({});
  const [tickerBasicsMap, setTickerBasicsMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isEditingTickers, setIsEditingTickers] = useState(false);

  // List management (match ListDaily behavior)
  const {
    userLists,
    currentListId,
    isLoadingUserLists,
    setUserLists,
    setCurrentListId,
    needsUserListsReload,
    needsListDataReload,
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  } = useUserListManagement({
    isAuthenticated: isAuthenticated && !isPublicList,
    tickerList,
    listName,
    setTickerList,
    setListName,
    refreshUsageLimitsRef,
    returnPath: '/performance',
  });

  // Ensure a current list is selected so tickers load (and we can trigger sync/perf calls).
  // This is a safety net for cases where cache/default-list resolution lags behind initial render.
  useEffect(() => {
    if (isPublicList) return;
    if (!isAuthenticated) return;
    if (isLoadingUserLists) return;
    if (currentListId !== null) return;
    if (userLists.length === 0) return;

    const nextId = userLists[0].id;
    setCurrentListId(nextId);
    cacheCurrentListId(nextId);
  }, [isPublicList, isAuthenticated, isLoadingUserLists, currentListId, userLists, setCurrentListId]);

  const {
    hiddenStatistics: privateHiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  } = useListPreferences({
    isAuthenticated: isAuthenticated && !isPublicList,
    currentListId: isPublicList ? null : currentListId,
    pageType: 'performance',
    persistToSessionStorageImmediately: false,
  });

  const hiddenStatistics = isPublicList && publicListData?.listPreferences?.performance_indicators_to_hide
    ? publicListData.listPreferences.performance_indicators_to_hide
    : privateHiddenStatistics;

  // Save changes (same structure as ListDaily)
  const handleSaveChanges = useCallback(async (saveTempTickerList: string[], saveTempListName: string) => {
    if (isPublicList) return;

    // Unauthenticated: persist list + name + hidden stats to session, then exit edit mode.
    // (useEditMode calls onSaveChanges on Done; if we return without setIsEditingTickers(false), stay stuck in edit mode.)
    if (!isAuthenticated) {
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(saveTempTickerList));
      sessionStorage.setItem('stocktable-list-name', saveTempListName);
      setTickerList(saveTempTickerList);
      setListName(saveTempListName);
      await savePreferences(hiddenStatistics);
      setIsEditingTickers(false);
      return;
    }

    // Determine listId
    let listId = currentListId;
    if (listId === null) {
      try {
        const defaultListResponse = await userApi.getDefaultList();
        if (defaultListResponse.defaultUserTickerListId) {
          listId = defaultListResponse.defaultUserTickerListId;
        } else if (userLists.length > 0) {
          listId = userLists[0].id;
        }
      } catch {
        if (userLists.length > 0) listId = userLists[0].id;
      }
    }

    if (!listId) return;

    // Update tickers order
    await userTickerListApi.updateTickerMappings(
      listId,
      saveTempTickerList.map((ticker_symbol, idx) => ({ ticker_symbol, order_num: idx }))
    );

    // Update list name if changed
    if (saveTempListName !== listName) {
      await userTickerListApi.updateListName(listId, saveTempListName);
      setListName(saveTempListName);
      cacheCurrentListName(saveTempListName);
    }

    // Save preferences
    await savePreferences(hiddenStatistics);

    // Persist session state
    sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(saveTempTickerList));
    sessionStorage.setItem('stocktable-list-name', saveTempListName);
    setTickerList(saveTempTickerList);
    clearListCache(listId);
    // Exit edit mode (useEditMode does not auto-exit when using onSaveChanges)
    setIsEditingTickers(false);
  }, [isPublicList, isAuthenticated, currentListId, userLists, listName, savePreferences, hiddenStatistics]);

  const { tempTickerList, tempListName, setTempTickerList, setTempListName, handleStartEdit, handleCancelEdit, handleDoneEdit, handleSave } = useEditMode({
    tickerList,
    listName,
    setTickerList,
    setListName,
    isEditingTickers,
    setIsEditingTickers,
    returnPath: '/performance',
    onSaveChanges: handleSaveChanges,
  } as any);

  const { draggedTicker, dragOverTicker, droppedTicker, handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd } =
    useDragAndDrop({
      tickerList: tempTickerList,
      setTickerList: setTempTickerList,
    } as any);

  const {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
  } = useModals({
    currentListId,
    isAuthenticated: isAuthenticated && !isPublicList,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete: () => {
      setCurrentListId(null);
      setListName('Main List');
      setTickerList([]);
      setHiddenStatistics([]);
    },
  });

  // Sync/polling state (copied from ListTechnicals)
  const [syncingTickers, setSyncingTickers] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem('stocktable-syncing-tickers');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [unsyncedTickers, setUnsyncedTickers] = useState<Set<string>>(new Set());
  const [pollingLimitReached, setPollingLimitReached] = useState<Set<string>>(new Set());
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingSyncRef = useRef(false);
  const unsyncedTickersRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    unsyncedTickersRef.current = unsyncedTickers;
  }, [unsyncedTickers]);

  const clearPollingInterval = useCallback(() => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
  }, [clearPollingInterval]);

  // Keep syncing tickers persisted so other tabs (e.g. ListTechnicals) don't re-hydrate stale "syncing" state.
  const setSyncingTickersPersist: React.Dispatch<React.SetStateAction<Set<string>>> = useCallback(
    (action) => {
      setSyncingTickers((prev) => {
        const next = typeof action === 'function' ? (action as any)(prev) : action;
        sessionStorage.setItem('stocktable-syncing-tickers', JSON.stringify(Array.from(next)));
        return next;
      });
    },
    []
  );

  const handleRateLimit = useCallback(() => {
    stopPolling();
    setSyncingTickersPersist(new Set());
    setUnsyncedTickers(new Set());
  }, [stopPolling]);

  const getTickerData = useCallback(
    (symbol: string): TickerDisplayData | undefined => {
      const basics = tickerBasicsMap[symbol];
      if (!basics) {
        return { ticker: symbol, company: symbol } as any;
      }
      return {
        ticker: symbol,
        company: basics.ticker_name || basics.tickerName || symbol,
        ticker_basics_id: basics.id || basics.ticker_basics_id,
      } as any;
    },
    [tickerBasicsMap]
  );

  const formatStatisticValue = useCallback(
    (statisticKey: string, ticker: TickerDisplayData) => {
      return formatPerformanceStatisticValue(statisticKey, ticker, {
        tickerPerformanceData,
        syncingTickers,
        unsyncedTickers,
        pollingLimitReached,
      });
    },
    [tickerPerformanceData, syncingTickers, unsyncedTickers, pollingLimitReached]
  );

  const getStatisticColor = useCallback(
    (statisticKey: string, ticker: TickerDisplayData) => {
      return getPerformanceStatisticColor(statisticKey, ticker, { tickerPerformanceData });
    },
    [tickerPerformanceData]
  );

  const fetchPerformanceData = async (syncedTickers: any[], symbolsFallback?: string[]): Promise<TickerPerformanceData> => {
    const api = isAuthenticated ? userTickerApi : tickerApi;

    // Preferred path: we have basics IDs from sync-status response.
    if (syncedTickers.length > 0) {
      const tickerBasicsIds = syncedTickers.map((t: any) => t.ticker_basics_id);
      const response = await api.getTickerPerformance({ tickerBasicsIds });
      const byId: Record<number, any> = {};
      (response.data || []).forEach((row: any) => {
        byId[row.tickerBasicsId] = row;
      });

      const next: TickerPerformanceData = {};
      for (const t of syncedTickers) {
        const row = byId[t.ticker_basics_id];
        const perf: PerformanceRow | null = row
          ? {
              ticker_basics_id: t.ticker_basics_id,
              date: row.date ? new Date(row.date).toISOString().slice(0, 10) : null,
              ytd_return: row.ytdReturn ?? null,
              one_m_return: row.oneMReturn ?? null,
              three_m_return: row.threeMReturn ?? null,
              six_m_return: row.sixMReturn ?? null,
              nine_m_return: row.nineMReturn ?? null,
              one_y_return: row.oneYReturn ?? null,
              two_y_return: row.twoYReturn ?? null,
              three_y_return: row.threeYReturn ?? null,
              four_y_return: row.fourYReturn ?? null,
              five_y_return: row.fiveYReturn ?? null,
            }
          : null;
        next[t.ticker_symbol] = { performance: perf };
      }
      return next;
    }

    // Fallback path: call by ticker symbols so we still hit the endpoint and can show data when available.
    const symbols = (symbolsFallback || []).map((s) => String(s).toUpperCase());
    if (symbols.length === 0) return {};

    const response = await api.getTickerPerformance({ tickerSymbols: symbols });
    const next: TickerPerformanceData = {};
    (response.data || []).forEach((row: any) => {
      const symbol = String(row.tickerSymbol || '').toUpperCase();
      if (!symbol) return;
      const basicsId = row.tickerBasicsId;
      next[symbol] = {
        performance: {
          ticker_basics_id: basicsId,
          date: row.date ? new Date(row.date).toISOString().slice(0, 10) : null,
          ytd_return: row.ytdReturn ?? null,
          one_m_return: row.oneMReturn ?? null,
          three_m_return: row.threeMReturn ?? null,
          six_m_return: row.sixMReturn ?? null,
          nine_m_return: row.nineMReturn ?? null,
          one_y_return: row.oneYReturn ?? null,
          two_y_return: row.twoYReturn ?? null,
          three_y_return: row.threeYReturn ?? null,
          four_y_return: row.fourYReturn ?? null,
          five_y_return: row.fiveYReturn ?? null,
        },
      };
    });

    // Populate basics map from performance response so table headers show names consistently.
    if (Object.keys(next).length > 0) {
      const nextBasics: Record<string, any> = { ...tickerBasicsMap };
      (response.data || []).forEach((row: any) => {
        const symbol = String(row.tickerSymbol || '').toUpperCase();
        if (!symbol) return;
        nextBasics[symbol] = { id: row.tickerBasicsId, ticker_name: symbol };
      });
      setTickerBasicsMap(nextBasics);
    }

    return next;
  };

  // Add ticker (shared hook) - must be defined after setSyncingTickers exists
  const { handleAddTicker: handleAddTickerHook } = useAddTicker({
    isAuthenticated,
    tickerList: isEditingTickers ? (tempTickerList || []) : tickerList,
    setTickerList: isEditingTickers ? (setTempTickerList as any) : setTickerList,
    setSyncingTickers: setSyncingTickersPersist,
    tempTickerList: tempTickerList || [],
    setTempTickerList: setTempTickerList as any,
    isEditingTickers,
    initializeEmptyData: useCallback((t: string) => {
      // ensure table renders placeholders while syncing
      setTickerPerformanceData((prev) => ({
        ...prev,
        [t]: { performance: null },
      }));
    }, []),
    onTickerNameUpdate: useCallback((t: string, tickerName: string) => {
      // best-effort: keep basics map populated so header hover shows a name
      setTickerBasicsMap((prev) => ({
        ...prev,
        [t]: { ...(prev[t] || {}), ticker_name: tickerName, id: (prev[t] || {}).id },
      }));
    }, []),
    onSyncComplete: useCallback(async (_t: string, tickerStatus: any) => {
      // tickerStatus comes from check-sync-status and includes ticker_basics_id + ticker_symbol
      if (!tickerStatus?.ticker_basics_id || !tickerStatus?.ticker_symbol) return;

      // Update basics map (id mapping) from sync response
      setTickerBasicsMap((prev) => ({
        ...prev,
        [tickerStatus.ticker_symbol]: {
          id: tickerStatus.ticker_basics_id,
          ticker_name: tickerStatus.ticker_name || tickerStatus.ticker_symbol,
        },
      }));

      // Fetch performance for just-synced ticker
      const perfData = await fetchPerformanceData([tickerStatus], [tickerStatus.ticker_symbol]);
      setTickerPerformanceData((prev) => ({ ...prev, ...perfData }));

      if (tickerStatus.last_sync_ended_at) {
        const d = new Date(tickerStatus.last_sync_ended_at);
        if (!isNaN(d.getTime())) setLastUpdated(d);
      }
    }, [fetchPerformanceData]),
    saveListImmediately: useCallback(async (tickers: string[]) => {
      if (isPublicList) return;
      const uniqueTickers = Array.from(new Set(tickers));

      // Guests: persist list so add-ticker + refresh still shows the column (ListProsCons pattern).
      if (!isAuthenticated) {
        sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(uniqueTickers));
        return;
      }

      let listId = currentListId;
      if (listId == null) {
        if (userLists.length > 0) listId = userLists[0].id;
      }
      if (listId == null) return;

      await userTickerListApi.updateTickerMappings(
        listId,
        uniqueTickers.map((ticker_symbol, idx) => ({ ticker_symbol, order_num: idx }))
      );
      clearListCache(listId);
      needsListDataReload.current = true;
    }, [isPublicList, isAuthenticated, currentListId, userLists, needsListDataReload]),
    onError: useCallback((_t: string, err: Error) => {
      setError(err.message || 'Failed to add ticker');
    }, []),
    pollingInterval: 1000,
    maxPollAttempts: 30,
  } as any);

  const handleRemoveTicker = useCallback((symbol: string) => {
    setTempTickerList(tempTickerList.filter((t: string) => t !== symbol));
  }, [tempTickerList, setTempTickerList]);

  const handleAddTicker = useCallback(async (symbol: string) => {
    await handleAddTickerHook(symbol);
  }, [handleAddTickerHook]);

  const handleShareList = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleShareModalClose = useCallback(() => setIsShareModalOpen(false), []);
  const handleShareModalUpdate = useCallback(() => {}, []);

  const checkSyncAndFetchDataForTickers = async (tickersToCheck: string[], attemptNumber: number = 1) => {
    if (pollingStoppedRef.current) return;
    if (isCheckingSyncRef.current) return;
    isCheckingSyncRef.current = true;
    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkTickerSyncStatus({ ticker_symbols: tickersToCheck });

      const tickers = syncResponse?.data?.tickers || [];
      const synced = tickers.filter((t: any) => t.is_synced === true);
      const unsynced = tickers.filter((t: any) => !t.is_synced);

      setUnsyncedTickers(new Set(unsynced.map((t: any) => t.ticker_symbol)));

      // Populate basics map using sync-status response (enough for display + id mapping)
      if (synced.length > 0) {
        const nextBasics: Record<string, any> = { ...tickerBasicsMap };
        synced.forEach((t: any) => {
          nextBasics[t.ticker_symbol] = { id: t.ticker_basics_id, ticker_name: t.ticker_symbol };
        });
        setTickerBasicsMap(nextBasics);
      }

      const perfData = await fetchPerformanceData(synced, tickersToCheck);
      setTickerPerformanceData((prev) => ({ ...prev, ...perfData }));
      // Safety: if we fetched data for synced tickers, ensure they can't remain in syncing/unsynced pulse state.
      if (synced.length > 0) {
        const syncedSymbols = synced.map((t: any) => t.ticker_symbol);
        setSyncingTickersPersist((prev) => {
          const next = new Set(prev);
          syncedSymbols.forEach((s: string) => next.delete(s));
          return next;
        });
        setUnsyncedTickers((prev) => {
          const next = new Set(prev);
          syncedSymbols.forEach((s: string) => next.delete(s));
          return next;
        });
      }

      if (synced.length > 0) {
        const lastSyncDates = synced
          .filter((t: any) => t.last_sync_ended_at)
          .map((t: any) => new Date(t.last_sync_ended_at));
        if (lastSyncDates.length > 0) {
          setLastUpdated(new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime()))));
        }
      }

      // stop syncing state for tickers that are now synced
      setSyncingTickersPersist((prev) => {
        const next = new Set(prev);
        synced.forEach((t: any) => next.delete(t.ticker_symbol));
        return next;
      });

      // polling cutoff
      const maxAttempts = 20;
      if (unsynced.length > 0 && attemptNumber >= maxAttempts) {
        setPollingLimitReached(new Set(unsynced.map((t: any) => t.ticker_symbol)));
        stopPolling();
      }
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        handleRateLimit();
      } else {
        setError(err?.message || 'Failed to load performance data');
      }
    } finally {
      isCheckingSyncRef.current = false;
    }
  };

  const checkSyncAndFetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // This run is an explicit fetch; ensure polling isn't "stuck" stopped due to effect cleanup.
    pollingStoppedRef.current = false;
    clearPollingInterval();

    const list = tickerList.length > 0 ? tickerList : (!isAuthenticated && !isPublicList ? DEFAULT_TICKERS : []);
    if (list.length === 0) {
      setLoading(false);
      return;
    }

    await checkSyncAndFetchDataForTickers(list, 1);

    // Start polling for any unsynced
    stopPolling();
    pollingStoppedRef.current = false;
    clearPollingInterval();

    let attempt = 1;
    syncPollingIntervalRef.current = setInterval(() => {
      attempt += 1;
      const symbols = Array.from(unsyncedTickersRef.current);
      if (symbols.length === 0) {
        if (syncPollingIntervalRef.current) clearInterval(syncPollingIntervalRef.current);
        return;
      }
      checkSyncAndFetchDataForTickers(symbols, attempt);
    }, 3000);

    setLoading(false);
  }, [tickerList, isAuthenticated, isPublicList, stopPolling, clearPollingInterval]);

  useEffect(() => {
    checkSyncAndFetchData();
    return () => {
      // Cleanup should only clear timers; don't mark polling as "stopped" for the next run.
      clearPollingInterval();
    };
  }, [checkSyncAndFetchData, clearPollingInterval]);

  const tabs = <TabBar activeTab="performance" disabled={isEditingTickers} publicUuid={publicUuid} />;
  const handleTickerClick = useCallback(
    (tickerSymbol: string) => {
      const sym = String(tickerSymbol || '').toUpperCase();
      if (!sym) return;
      if (publicUuid) {
        navigate(`/ticker/${sym}/performance?public_uuid=${publicUuid}`);
      } else {
        navigate(`/ticker/${sym}/performance`);
      }
    },
    [navigate, publicUuid]
  );

  if (publicUuid && isPublicListNotFound) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        {!isAuthenticated && <PublicListPromoBar />}
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-gray-700 dark:text-text-primary">
              {publicListError || 'This list does not exist or is no longer available.'}
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <StockTableLayout
          tickerList={isEditingTickers ? tempTickerList : tickerList}
          tickerData={Object.keys(tickerPerformanceData).map((symbol) => getTickerData(symbol)).filter(Boolean) as TickerDisplayData[]}
          loading={loading || isLoadingPublicList}
          error={error || (isPublicList ? publicListError : null)}
          lastUpdated={lastUpdated}
          isEditingTickers={isPublicList ? false : isEditingTickers}
          isPublicList={isPublicList}
          showPublicListPromoBar={isPublicList && !isAuthenticated}
          hiddenStatistics={hiddenStatistics}
          draggedTicker={draggedTicker}
          dragOverTicker={dragOverTicker}
          droppedTicker={droppedTicker}
          statistics={PERFORMANCE_STATISTICS}
          categoryHeaders={CATEGORY_HEADERS}
          getTickerData={getTickerData}
          formatStatisticValue={formatStatisticValue as any}
          getStatisticColor={getStatisticColor as any}
          lockedStatistics={[]}
          proOnlyStatistics={[]}
          lockedTickers={[]}
          onRefetch={() => {
            setLoading(true);
            checkSyncAndFetchData().finally(() => setLoading(false));
          }}
          onTickerClick={handleTickerClick}
          onRemoveTicker={isPublicList ? () => {} : handleRemoveTicker}
          onDragStart={isPublicList ? () => {} : handleDragStart}
          onDragOver={isPublicList ? () => {} : handleDragOver}
          onDragLeave={isPublicList ? () => {} : handleDragLeave}
          onDrop={isPublicList ? () => {} : handleDrop}
          onDragEnd={isPublicList ? () => {} : handleDragEnd}
          onHideStatistic={isPublicList ? () => {} : handleHideStatistic}
          onShowStatistic={isPublicList ? () => {} : handleShowStatistic}
          onCategoryInfoClick={undefined}
          listName={listName}
          onStartEdit={isPublicList ? () => {} : handleStartEdit}
          onCancelEdit={isPublicList ? () => {} : handleCancelEdit}
          onDoneEdit={isPublicList ? () => {} : handleDoneEdit}
          onAddTicker={isPublicList ? () => {} : handleAddTicker}
          onSave={handleSave}
          tempListName={tempListName}
          onTempListNameChange={setTempListName}
          userLists={isPublicList ? [] : userLists}
          currentListId={isPublicList ? undefined : (currentListId || undefined)}
          onListChange={isPublicList ? undefined : handleListChange}
          onCreateNewList={isPublicList ? undefined : handleCreateNewList}
          onCreateNewListWithName={isPublicList ? undefined : handleCreateNewListWithName}
          onDeleteList={handleDeleteList}
          showDeleteButton={!isPublicList && isAuthenticated && userLists.length > 1}
          onShareList={handleShareList}
          showShareButton={!isPublicList && isAuthenticated && currentListId !== null}
          onRefreshUsageLimits={() => refreshUsageLimitsRef.current?.()}
          tabs={tabs}
          showRefreshButton={false}
          showDelayedPricesText={false}
          showLastUpdatedDate={true}
        />

        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>

      <DeleteListConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        listName={listName}
      />

      <ErrorDialog isOpen={isErrorDialogOpen} onClose={handleErrorDialogClose} title="Error" message={errorMessage} />

      <ShareListModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        listId={currentListId}
        currentList={userLists.find(l => l.id === currentListId) || null}
        onUpdate={handleShareModalUpdate}
        isListLocked={false}
      />
    </>
  );
};

export default ListPerformance;

